<template>
  <div id="mondai-app">
    <!-- Navbar is now at the root so it shows instantly on refresh -->
    <AppNavbar v-if="showNavbar" />
    
    <!-- RouterView handles the page content -->
    <RouterView />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed } from "vue";
import { RouterView, useRoute } from "vue-router";
import AppNavbar from "@/components/ui/AppNavbar.vue";
import { useAuthStore } from "@/stores/auth.store";
import { useUiStore } from "@/stores/ui.store";
import { useContentStore } from "@/stores/content.store";
import { useAdminStore } from "@/stores/admin.store";
import { useTheme } from "@/composables/useTheme";
import { config } from "@/config";
import { supabase } from "@/lib/supabase";

const authStore = useAuthStore();
const uiStore = useUiStore();
const contentStore = useContentStore();
const { initializeTheme } = useTheme();
const route = useRoute();

const showNavbar = computed(() => {
  // 1. Hide navbar on auth and onboarding routes
  if (route.path.startsWith("/auth")) return false;
  if (route.path.startsWith("/onboarding")) return false;

  // 2. If it's a public route (Privacy/Terms), only show if authenticated
  if (route.meta.public) {
    return authStore.isAuthenticated;
  }

  // 3. For all other routes, show if authenticated or still initializing
  // (We show during loading to prevent layout shift if the user is logged in)
  return authStore.isAuthenticated || authStore.isLoading;
});

let lastVisibilityChange = Date.now();

// Config-driven thresholds
const IDLE_THRESHOLD_MS = config.session.idleThresholdMinutes * 60 * 1000;
const FORCE_RELOAD_MS = config.session.forceReloadMinutes * 60 * 1000;
const DATA_STALE_MS = config.session.dataStaleMinutes * 60 * 1000;

/**
 * Handle visibility change — kick-start Supabase's auto-refresh when tab
 * becomes visible after idle. Supabase's autoRefreshToken pauses when the
 * tab is hidden and resumes automatically, but after extended idle the
 * underlying TCP connection may be stale. Calling startAutoRefresh()
 * forces an immediate re-check.
 */
let visibilityTimer: any = null;

async function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    // Small debounce to avoid multiple triggers on rapid tab focus
    if (visibilityTimer) clearTimeout(visibilityTimer);
    visibilityTimer = setTimeout(async () => {
      const idleTime = Date.now() - lastVisibilityChange;

      // Also check localStorage for extended idle (survives page state reset)
      const storedHiddenTime = localStorage.getItem("mondai-tab-hidden-time");
      let extendedIdleTime = 0;
      if (storedHiddenTime) {
        extendedIdleTime = Date.now() - parseInt(storedHiddenTime, 10);
      }

      const effectiveIdleTime = Math.max(idleTime, extendedIdleTime);
      uiStore.setTabVisible(true);
      localStorage.removeItem("mondai-tab-hidden-time");

      // Force reload after extended idle for a completely clean state
      if (effectiveIdleTime > FORCE_RELOAD_MS) {
        window.location.reload();
        return;
      }

      // After moderate idle, kick-start Supabase's built-in auto-refresh.
      if (effectiveIdleTime > IDLE_THRESHOLD_MS && authStore.isAuthenticated) {
        supabase.auth.startAutoRefresh();
      }

      if (effectiveIdleTime > DATA_STALE_MS) {
        console.log(`[App] ${new Date().toISOString()} Data marked as stale after ${Math.round(effectiveIdleTime / 1000 / 60)} min idle`);
        uiStore.invalidateAllData();
      }
    }, 200); // 200ms debounce
  } else {
    // Tab is hidden — record the time
    if (visibilityTimer) clearTimeout(visibilityTimer);
    lastVisibilityChange = Date.now();
    localStorage.setItem("mondai-tab-hidden-time", String(Date.now()));
    uiStore.setTabVisible(false);
  }
}

onMounted(async () => {
  // 1. Initialize theme (Local only, instant)
  initializeTheme();

  // 2. Wait for auth state with a Fail-Fast Guard
  // The store was kicked off in main.ts, we just wait for it here.
  console.log(`[App] Waiting for auth to initialize...`);
  
  const authWaitGuard = new Promise<void>((resolve) => {
    if (!authStore.isLoading) {
      resolve();
      return;
    }
    const unwatch = authStore.$subscribe((_mutation, state) => {
      if (!state.isLoading) {
        unwatch();
        resolve();
      }
    });
  });

  const timeoutGuard = new Promise<void>((resolve) => {
    setTimeout(() => {
      if (authStore.isLoading) {
        console.warn(`[App] Auth initialization taking too long (>2s). Unblocking UI...`);
      }
      resolve();
    }, 2000);
  });

  // Wait for either auth to finish or the 2s timeout
  await Promise.race([authWaitGuard, timeoutGuard]);
  console.log(`[App] Initial render unblocked. Auth status: ${authStore.isAuthenticated ? 'Logged In' : 'Pending/Guest'}`);

  // 3. Load data in background (Non-blocking)
  const adminStore = useAdminStore();
  Promise.allSettled([
    contentStore.loadContent(),
    adminStore.loadSettings()
  ]).then(() => {
    console.log(`[App] Global content/settings loaded`);
  }).catch(err => {
    console.error(`[App] Background load failed:`, err);
  });

  // 4. Listen for visibility changes
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<style>
/* Global app styles are imported in main.ts */
#mondai-app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.initial-load-shell {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-primary);
  color: var(--color-text-muted);
}
</style>
